import { useEffect } from "react";
import { getAnalytics } from "@tm/insurance/analytics";
import { TextNodeType } from "../../CommonLandingMweb.types";
import {
  Container,
  DateText,
  Destination,
  Heading,
  Meta,
  PlanType,
  ScrollRow,
  SearchCard,
} from "./Styles";
import FormattedText from "../../../FormattedText/FormattedText";
import { formatRecentSearchTravelRange } from "../../../../utils";
import type { RecentSearchNavigateItem } from "../../../../utils/recentSearchNavigation";

export interface RecentSearchItem extends RecentSearchNavigateItem {
  title?: TextNodeType;
  countryName?: TextNodeType;
  travelDate?: TextNodeType | string;
}

interface RecentSearchesProps {
  title?: TextNodeType;
  recentSearches?: RecentSearchItem[];
  /** Listing navigation — same contract as desktop `nextPageDataCallTrigger(buildRecentSearchNextPageRequest(item))`. */
  onSearchPress?: (search: RecentSearchItem, index: number) => void;
}

const formatAdults = (n?: number) => {
  const count = n ?? 1;
  return `${count} ${count === 1 ? "Adult" : "Adults"}`;
};

const analytics = getAnalytics();

export const RecentSearches = ({
  title,
  recentSearches = [],
  onSearchPress,
}: RecentSearchesProps) => {
  useEffect(() => {
    analytics?.trackCommonLandingEvents({
      actionType: "recent_searches_seen",
    });
  }, []);

  if (!recentSearches.length) return null;

  return (
    <Container>
      <Heading>
        <FormattedText value={title || "Recent Searches"} />
      </Heading>
      <ScrollRow>
        {recentSearches.map((item, index) => {
          const rangeFromDuration = formatRecentSearchTravelRange(
            item.travelDuration?.startDate,
            item.travelDuration?.endDate
          );
          const td = item.travelDate;
          const dateEl =
            td != null && typeof td === "object" ? (
              <DateText>
                <FormattedText value={td as TextNodeType} />
              </DateText>
            ) : typeof td === "string" && td ? (
              <DateText>{td}</DateText>
            ) : rangeFromDuration ? (
              <DateText>{rangeFromDuration}</DateText>
            ) : null;

          return (
            <SearchCard
              key={`rs-${index}`}
              onClick={() => {
                analytics?.trackCommonLandingEvents({
                  actionType: `recent_search_${index}_clicked`,
                });
                onSearchPress?.(item, index);
              }}
            >
              <PlanType>
                <FormattedText value={item.title || "Recent trip"} />
              </PlanType>
              <Destination>
                <FormattedText value={item.countryName} />
              </Destination>
              <Meta>{formatAdults(item.travellerNum)}</Meta>
              {dateEl}
            </SearchCard>
          );
        })}
      </ScrollRow>
    </Container>
  );
};
